import { useNavigate } from 'react-router-dom'
import MultiStepForm from '../components/MultiStepForm.jsx'
import WizardAside from '../components/WizardAside.jsx'
import { useAuth } from '../auth.jsx'
import { inviteMember } from '../lib/team.js'
import { ROLE_DEFS, permissionsForRole, rolesAssignableBy, useRbac } from '../lib/rbac.js'
import RolePermissions from '../components/RolePermissions.jsx'
import { logEvent } from '../lib/events.js'

// Inviting a teammate is a guided multi-step flow (details → access → note),
// mirroring the New Sequence wizard. On finish it creates the Cognito invite
// (falling back to local storage in dev) and returns to Collaboration. Role
// choices come from the live RBAC defs, limited to what the inviter can grant.

// Illustrative mock — explicitly captioned "Example" so the placeholder persona
// can't be mistaken for a real teammate.
function InvitePreview() {
  return (
    <div className="w-[640px] max-w-full rounded-[14px] border border-line bg-surface p-[22px] font-sora shadow-card">
      <div className="mb-[6px] flex items-center gap-x-2 text-[12px] text-dim">
        Team invite
        <span className="rounded-full border border-line bg-panel px-2 py-px text-[10.5px] font-semibold uppercase tracking-[0.04em] text-faint">Example</span>
      </div>
      <div className="flex items-center gap-x-3">
        <span className="grid h-[46px] w-[46px] flex-none place-items-center rounded-[14px] bg-[#18181b] text-[15px] font-semibold tracking-[0.02em] text-white">
          JR
        </span>
        <div className="min-w-0">
          <div className="text-[20px] font-semibold tracking-[-0.01em] text-ink">Jordan Rivera</div>
          <div className="text-[13px] text-dim">jordan@acmeanalytics.com</div>
        </div>
        <span className="ml-auto whitespace-nowrap rounded-full bg-primary-tint px-[10px] py-[3px] text-[11px] font-bold text-primary-dark">
          Member
        </span>
      </div>
      <div className="mt-4 rounded-[10px] border border-line bg-panel p-4">
        <div className="mb-1 text-[12px] font-semibold text-dim">Invitation email</div>
        <div className="text-[13.5px] leading-[1.55] text-ink">
          You’ve been invited to join <b>Revailo Sales</b>. Click below to set your
          password and start collaborating on leads and sequences.
        </div>
        <div className="mt-3 inline-flex rounded-[8px] bg-primary px-[14px] py-2 text-[12.5px] font-semibold text-white">
          Accept invite
        </div>
      </div>
    </div>
  )
}

export default function InviteTeam() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { role: myRole, perms: myPerms } = useRbac()
  const assignable = rolesAssignableBy(myRole)

  // The wizard starts from the Sales Rep preset clamped to the inviter's own
  // grants: an inviter can never hand out access they don't hold, even by
  // clicking straight through the access step.
  const defaultRole = assignable.includes('rep') ? 'rep' : assignable[assignable.length - 1] || 'viewer'
  const repSeed = [...permissionsForRole('rep')].filter((k) => myPerms.has(k))
  const seedIsFullRep = defaultRole === 'rep' && repSeed.length === permissionsForRole('rep').size
  const initialData = seedIsFullRep
    ? { role: 'rep', permissions: null }
    : { role: defaultRole, permissions: repSeed }

  const steps = [
    {
      id: 'details',
      // `required` only checks non-empty, so the email format is checked here.
      canContinue: (d) => {
        if (!(d.name || '').trim()) return 'Enter their full name to continue.'
        const email = (d.email || '').trim()
        if (!email) return 'Enter their work email to continue.'
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return `“${email}” isn't a valid email address.`
        return true
      },
      title: 'Who are you inviting?',
      description: 'We’ll create their account and email them a link to set a password.',
      fields: [
        {
          name: 'name',
          label: 'Full name',
          type: 'text',
          placeholder: 'Jordan Rivera',
          required: true,
          requiredMessage: 'Enter their full name',
        },
        {
          name: 'email',
          label: 'Work email',
          type: 'email',
          placeholder: 'name@company.com',
          required: true,
          requiredMessage: 'Enter a valid work email',
        },
      ],
    },
    {
      id: 'access',
      title: 'Set their access',
      description:
        'Pick a role preset to auto-fill the grid, or toggle individual permissions for a custom setup.',
      render: (data, setField) => (
        <div className="mt-[26px]">
          <RolePermissions
            value={{
              role: data.role || defaultRole,
              // `permissions` is meaningful when it's an array — including an
              // empty one — so never coerce it to null (that would re-expand
              // the clamped seed back to the full preset).
              permissions: Array.isArray(data.permissions) ? data.permissions : null,
            }}
            onChange={(next) => {
              setField('role', next.role)
              setField('permissions', next.permissions)
            }}
            presets={assignable}
            grantable={myPerms}
          />
        </div>
      ),
    },
    {
      id: 'note',
      title: 'Add a personal note',
      description: 'Optional — sent along with the invite request.',
      fields: [
        {
          name: 'message',
          label: 'Note (optional)',
          type: 'textarea',
          placeholder: 'Hey! Adding you to our Revailo Sales workspace so we can split the outreach.',
          rows: 4,
        },
      ],
    },
  ]

  async function handleComplete(data) {
    // Same clamp on the way out (defense in depth against any editor gap).
    let role = data.role || defaultRole
    let permissions = Array.isArray(data.permissions) ? data.permissions : null
    if (!assignable.includes(role)) {
      permissions = [...(permissions || [...permissionsForRole(role)])].filter((k) => myPerms.has(k))
      role = defaultRole
    }
    const accessLabel = Array.isArray(permissions)
      ? 'custom permissions'
      : ROLE_DEFS[role]?.label || role
    let mode = 'api'
    try {
      ;({ mode } = await inviteMember({
        email: data.email,
        name: data.name,
        role,
        permissions: Array.isArray(permissions) ? permissions : undefined,
        message: data.message || '',
      }))
    } catch (err) {
      // Only real failure in this flow is a duplicate invite — surface it and
      // keep the user on the wizard so their input isn't lost.
      window.alert(err.message || 'Could not send the invite.')
      return
    }

    logEvent('member_invited', {
      actor: user?.email || '',
      actorName: user?.name || '',
      detail: `Invited ${data.name.trim()} (${data.email.trim().toLowerCase()}) with ${accessLabel}`,
    })

    navigate('/collaboration', {
      state: { invited: `${data.name.trim()} (${data.email.trim().toLowerCase()})`, mode },
    })
  }

  return (
    <MultiStepForm
      steps={steps}
      initialData={initialData}
      finalLabel="Send invite"
      onComplete={handleComplete}
      onExit={() => navigate('/collaboration')}
      backLabel="Back to team"
      aside={
        <WizardAside>
          <InvitePreview />
        </WizardAside>
      }
    />
  )
}
